import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import withApi from '../../components/higherOrderComponents/withApi';
import Modal from '../components/Modal';
import { useModal } from '../components/useModal';
import { offerForceRetry } from '../components/forceRetry';

/**
 * Overview — bookings and cancellations that need a human.
 *
 * Every call and every action is the old page's: `getOverview`, `retry`,
 * `mute`, `sendConfirmation`, `editComment`, and the same confirm prompts
 * guarding a retry. Dropping one of them would be a functional regression on a
 * page that exists to act on things, so the rewrite is presentation only.
 *
 * What is not carried over is the old page's *plumbing*: `Notification`,
 * `AriaModal` and the `Display*` helpers all render markup the old stylesheet
 * owns, and `AriaModal` portals to the app root — outside `.next-root`, where
 * this tree's styles cannot reach it and the old ones can. So the banner and the
 * detail panel are rendered in-tree instead, and the confirmations go through
 * `Modal`, which brings back the focus trap `AriaModal` was providing.
 */

const GUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const camelToWords = text => {
  const value = text == null ? 'null' : text;
  const spaced = value.replace(/([A-Z])/g, ' $1');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const statusTone = status => {
  if (status === 'Success') return 'ok';
  if (status === 'Failure') return 'bad';
  return 'neutral';
};

const formatDate = value => {
  if (!value) return '—';
  // The API sends CET wall-clock strings; parsing and reformatting them in the
  // browser's zone would shift them, so they are only tidied, never converted.
  return String(value).replace('T', ' ').slice(0, 16);
};

const OverviewInternal = ({ api }) => {
  const { ask, modalProps } = useModal();
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState(null);
  const [details, setDetails] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.overview.getOverview();
      // `?? []` only covers a missing value, not a wrong one: anything that is
      // not a list here reaches `rows.filter` and takes the page down. Same
      // guard as the dashboard's.
      setRows(Array.isArray(data?.data) ? data.data : []);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const say = (tone, text) => setNote({ tone, text });

  const ctx = signupId => `signup …${String(signupId).slice(-6)}`;

  const mute = async (signupId, isBooking) => {
    if (!GUID.test(signupId)) return;

    const response = await api.overview.mute(signupId, isBooking);
    if (response.success) {
      say('info', `${isBooking ? 'Booking' : 'Cancellation'} muted.`);
      load();
    } else {
      say('error', 'Unable to mute this signup.');
    }
  };

  const retry = (signup, isBooking) => {
    const what = isBooking ? 'booking' : 'cancellation';
    const status = isBooking ? signup.bookStatus : signup.cancellationStatus;
    const alreadyDone = status === 'Success';
    const failedBefore = status === 'Failure';

    // Retrying something that already worked, or that never failed, is almost
    // always a mistake. The old page asked twice; the dialog can say why once.
    ask({
      kind: alreadyDone ? 'danger' : 'confirm',
      title: alreadyDone
        ? `Retry a ${what} that already succeeded?`
        : `Retry this ${what}?`,
      context: `Retry ${what} · ${ctx(signup.id)}`,
      body: alreadyDone
        ? `This ${what} already came back Success. Sending it again asks the provider for the same slot a second time.`
        : failedBefore
          ? 'A fresh request goes to the external system with the same activity and court.'
          : `This ${what} has not failed, so there may be nothing to retry.`,
      warning: alreadyDone
        ? 'A second successful booking is not undone by this admin — you would have to cancel one by hand at the venue.'
        : undefined,
      confirmLabel: alreadyDone ? 'Retry anyway' : `Retry ${what}`,
      onConfirm: async () => {
        const result = await api.overview.retry(signup.id, isBooking, false);
        say(
          result.success ? 'info' : 'error',
          result.success
            ? 'Signup has been put back in the queue.'
            : (result.message || 'Unable to put the signup back in the queue.')
        );
        if (result.canForce) {
          offerForceRetry(ask, {
            what,
            context: `Retry ${what} · ${ctx(signup.id)}`,
            message: result.message,
            onConfirm: async () => {
              const forced = await api.overview.retry(signup.id, isBooking, true);
              say(
                forced.success ? 'info' : 'error',
                forced.success
                  ? 'Signup has been put back in the queue.'
                  : (forced.message || 'Unable to put the signup back in the queue.')
              );
              if (!forced.success) throw new Error('retry failed');
            }
          });
        }
        if (!result.success) throw new Error('retry failed');
      }
    });
  };

  const sendConfirmation = (signupId, isBooking) => {
    if (!GUID.test(signupId)) return;

    ask({
      kind: 'confirm',
      title: `Send the ${isBooking ? 'booking' : 'cancellation'} confirmation?`,
      context: `Confirm ${isBooking ? 'booking' : 'cancellation'} · ${ctx(signupId)}`,
      body: 'The confirmation email is sent to the user at the address on the signup.',
      confirmLabel: 'Send email',
      onConfirm: async () => {
        const response = await api.overview.sendConfirmation(signupId, isBooking);
        say(response.success ? 'info' : 'error',
          response.success ? 'Confirmation sent.' : 'Unable to confirm this signup.');
        if (response.success) load();
        else throw new Error('confirmation failed');
      }
    });
  };

  const editComment = (signupId, comment) => ask({
    kind: 'input',
    title: 'Internal comment',
    context: `Edit comment · ${ctx(signupId)}`,
    body: 'Visible to administrators only, on the signup.',
    label: 'Comment',
    placeholder: 'e.g. user called, waiting for confirmation',
    multiline: true,
    initialValue: comment || '',
    confirmLabel: 'Save comment',
    onConfirm: async next => {
      const response = await api.overview.editComment(signupId, next);
      say(response.success ? 'info' : 'error',
        response.success ? 'Comment changed.' : 'Unable to change the comment.');
      if (response.success) load();
      else throw new Error('comment failed');
    }
  });

  if (failed) {
    return (
      <div className="next-page">
        <div className="next-note next-note--error">
          Could not load the overview.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (rows === null) {
    return <div className="next-page next-muted">Loading…</div>;
  }

  const bookings = rows.filter(({ showInSignups }) => showInSignups);
  const cancellations = rows.filter(({ showInCancellation }) => showInCancellation);

  const table = (list, isBooking) => {
    if (list.length === 0) {
      return (
        <p className="next-empty">
          Nothing waiting — every {isBooking ? 'booking' : 'cancellation'} went through.
        </p>
      );
    }

    return (
      <div className="next-table-wrap">
        <table className="next-table next-table--wide next-table--stack">
          <thead>
            <tr>
              <th>Facility</th>
              <th>Sport</th>
              <th>Title</th>
              <th>User</th>
              <th>Signup (CET)</th>
              <th>Activity (CET)</th>
              <th>Result</th>
              <th>Secret</th>
              <th>Comment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map(row => {
              const status = isBooking ? row.bookStatus : row.cancellationStatus;

              return (
                <tr key={`${row.id}-${isBooking ? 'b' : 'c'}`}>
                  <td data-label="">
                    <a className="next-link" href={`/next/signup/${row.id}`}>
                      {row.provider || row.facilityName || '—'}
                    </a>
                    {row.isBundle && <span className="next-pill next-pill--neutral">bundle</span>}
                  </td>
                  <td data-label="Sport" className="next-muted">{row.sport}</td>
                  <td data-label="Title">{row.title}</td>
                  <td data-label="User" className="next-break">{row.userName}</td>
                  <td data-label="Signup (CET)" className="next-nowrap next-muted">{formatDate(row.signupDateCet)}</td>
                  <td data-label="Activity (CET)" className="next-nowrap">{formatDate(row.activityDateCet)}</td>
                  <td data-label="Result">
                    <span className={`next-pill next-pill--${statusTone(status)}`}>
                      {camelToWords(status)}
                    </span>
                  </td>
                  <td data-label="Secret" className="next-nowrap">{row.secretCode}</td>
                  <td data-label="Comment" className="next-muted">{row.signupComment}</td>
                  <td data-label="">
                    <div className="next-actions">
                      <button type="button" className="next-btn next-btn--sm" onClick={() => retry(row, isBooking)}>Retry</button>
                      <button type="button" className="next-btn next-btn--sm" onClick={() => mute(row.id, isBooking)}>Mute</button>
                      <button type="button" className="next-btn next-btn--sm" onClick={() => sendConfirmation(row.id, isBooking)}>Confirm</button>
                      <button type="button" className="next-btn next-btn--sm next-btn--quiet" onClick={() => setDetails(row)}>Details</button>
                      <button type="button" className="next-btn next-btn--sm next-btn--quiet" onClick={() => editComment(row.id, row.signupComment)}>Edit</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header">
        <h1>Overview</h1>
        <p className="next-muted">
          Bookings and cancellations that did not complete on their own.
        </p>
      </header>

      <Modal {...modalProps} />

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <section className="next-section">
        <h2>Bookings <span className="next-count">{bookings.length}</span></h2>
        {table(bookings, true)}
      </section>

      <section className="next-section">
        <h2>Cancellations <span className="next-count">{cancellations.length}</span></h2>
        {table(cancellations, false)}
      </section>

      {details && (
        <div className="next-overlay" onClick={() => setDetails(null)} role="presentation">
          <div className="next-dialog" onClick={event => event.stopPropagation()} role="dialog" aria-label="Signup details">
            <header className="next-dialog__head">
              <h2>Details</h2>
              <button type="button" className="next-btn next-btn--quiet" onClick={() => setDetails(null)}>Close</button>
            </header>
            <h3 className="next-dialog__label">Comment</h3>
            <pre className="next-pre">{details.comment || '—'}</pre>
            <h3 className="next-dialog__label">Credentials</h3>
            <pre className="next-pre">{details.credentials || '—'}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

OverviewInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Overview = withApi(OverviewInternal);

Overview.requiresLogin = true;

export default Overview;
